import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { logIn, useUserAuth } from '../../UserAuth';
import {
  archiveUserDeck,
  checkDeckMembership,
  joinDeck,
  leaveDeck,
  loadDeck,
  loadDeckMembers,
  removeDeckMember,
  renameDeck,
  rewriteDeckVerses,
  syncDeckMemberDisplayName,
  updateDeckAdminRole,
  updateDeckPace,
  updateDeckVisibility,
} from '../../utils/deck_service';
import { useViewportHeight } from '../../utils/viewportUtils';
import { Icon } from '../Icon';
import { MaterialHeader } from '../MaterialHeader';
import { DeckInfoTab } from './DeckInfoTab';
import { DeckLeaderboardTab } from './DeckLeaderboardTab';
import { DeckMemorizeTab } from './DeckMemorizeTab';
import { DeckSettingsTab } from './DeckSettingsTab';
import { getDeckTab } from './deck_route_utils';

export function MaterialDeck() {
  const { user } = useUserAuth();
  const history = useHistory();
  const location = useLocation();
  const viewportHeight = useViewportHeight();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const deckId = query.get('id');
  const [activeTab, setActiveTab] = useState(() => getDeckTab(location.search));
  const [deck, setDeck] = useState(null);
  const [membership, setMembership] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [membersLoadedAt, setMembersLoadedAt] = useState(null);
  const [membersError, setMembersError] = useState('');

  const showToast = useCallback(message => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  const refresh = useCallback(async () => {
    if (!deckId) {
      setError('A deck ID is required.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const nextDeck = await loadDeck(db, deckId);
      if (!nextDeck) {
        setError('Deck not found.');
        setDeck(null);
        return;
      }
      setDeck(nextDeck);
      if (user) {
        const nextMembership = await checkDeckMembership(db, user.uid, deckId);
        const syncedDisplayName = await syncDeckMemberDisplayName(
          db,
          user.uid,
          deckId,
          nextMembership,
          user.displayName
        );
        setMembership(syncedDisplayName
          ? { ...nextMembership, display_name: syncedDisplayName }
          : nextMembership);
      } else {
        setMembership(null);
      }
      setMembers([]);
      setMembersLoadedAt(null);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load this deck.');
    } finally {
      setLoading(false);
    }
  }, [deckId, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const membersNeeded = activeTab === 'leaderboard' || activeTab === 'settings';

  useEffect(() => {
    if (!membersNeeded || !membership || !deckId || membersLoadedAt) {
      return undefined;
    }
    let active = true;
    setMembersError('');
    loadDeckMembers(db, deckId)
      .then(nextMembers => {
        if (active) {
          setMembers(nextMembers);
          setMembersLoadedAt(new Date());
        }
      })
      .catch(loadError => {
        if (active) {
          setMembersError(loadError.message || 'Unable to load deck members.');
        }
      });
    return () => {
      active = false;
    };
  }, [deckId, membership, membersLoadedAt, membersNeeded]);

  useEffect(() => {
    const nextQuery = new URLSearchParams(location.search);
    if (nextQuery.get('tab') !== activeTab) {
      nextQuery.set('tab', activeTab);
      history.replace(`${location.pathname}?${nextQuery.toString()}`);
    }
  }, [activeTab, history, location.pathname, location.search]);

  const runAction = async (action, successMessage, options = {}) => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await action();
      if (options.navigateHome) {
        history.push('/');
        return;
      }
      await refresh();
      showToast(successMessage);
    } catch (actionError) {
      showToast(actionError.message || 'Unable to update this deck.');
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = () => {
    if (!user) {
      logIn();
      return;
    }
    if (busy || membership) {
      return;
    }
    runAction(() => joinDeck(db, user, deckId), 'Deck joined.');
  };

  const handleProgressChange = useCallback(summary => {
    if (!user) {
      return;
    }
    setMembers(current => current.map(member => (
      member.id === user.uid ? { ...member, ...summary } : member
    )));
  }, [user]);

  if (loading) {
    return (
      <div style={{ height: viewportHeight, display: 'grid', placeItems: 'center' }}>
        <md-circular-progress indeterminate></md-circular-progress>
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div style={{ height: viewportHeight, display: 'flex', flexDirection: 'column' }}>
        <MaterialHeader variant="back" title="Deck" onBack={() => history.push('/')} hideSettings />
        <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <Icon name="error" size="hero" style={{ color: 'var(--md-sys-color-error)' }} />
            <h1>{error || 'Deck not found.'}</h1>
            <md-filled-button onClick={refresh}>Retry</md-filled-button>
          </div>
        </main>
      </div>
    );
  }

  const tabs = [
    { id: 'memorize', icon: 'memorizeTab', label: 'Memorize' },
    { id: 'leaderboard', icon: 'deckLeaderboardTab', label: 'Leaderboard' },
    { id: 'info', icon: 'deckInfoTab', label: 'Info' },
    { id: 'settings', icon: 'deckSettingsTab', label: 'Settings' },
  ];
  const deckName = typeof deck.deck_name === 'string' && deck.deck_name.trim()
    ? deck.deck_name.trim()
    : 'Untitled Deck';
  const verseCount = Number.isInteger(deck.verse_count) && deck.verse_count >= 0
    ? deck.verse_count
    : Array.isArray(deck.verses) ? deck.verses.length : 0;

  return (
    <div style={{
      height: viewportHeight,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: 'var(--md-sys-color-surface)',
    }}>
      <MaterialHeader
        variant="back"
        title={deckName.length > 30 ? `${deckName.slice(0, 27)}...` : deckName}
        onBack={() => history.goBack()}
        hideSettings
      />
      {!membership && (
        <button
          type="button"
          {...(busy ? { disabled: true } : {})}
          onClick={handleJoin}
          style={{
            border: 0,
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
            padding: '10px 16px',
            backgroundColor: 'var(--md-sys-color-primary-container)',
            color: 'var(--md-sys-color-on-primary-container)',
            font: 'inherit',
            cursor: 'pointer',
          }}
        >
          {user ? 'Join this deck to save your progress.' : 'Sign in to join this deck.'}
        </button>
      )}
      <main style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {activeTab === 'memorize' && (
          <DeckMemorizeTab
            deck={deck}
            membership={membership}
            user={user}
            busy={busy}
            onSignIn={logIn}
            onJoin={handleJoin}
            onProgressChange={handleProgressChange}
          />
        )}
        {activeTab === 'leaderboard' && (
          membership ? (
            <DeckLeaderboardTab
              members={members}
              verseCount={verseCount}
              loading={!membersLoadedAt && !membersError}
              error={membersError}
              refreshedAt={membersLoadedAt}
            />
          ) : (
            <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: '16px' }}>
              {user ? (
                <md-filled-button {...(busy ? { disabled: true } : {})} onClick={handleJoin}>
                  Join to View Leaderboard
                </md-filled-button>
              ) : (
                <md-filled-button onClick={logIn}>Sign In to View Leaderboard</md-filled-button>
              )}
            </div>
          )
        )}
        {activeTab === 'info' && <DeckInfoTab deck={deck} />}
        {activeTab === 'settings' && (
          <DeckSettingsTab
            deck={deck}
            user={user}
            membership={membership}
            members={members}
            busy={busy}
            onSignIn={logIn}
            onJoin={handleJoin}
            onLeave={() => runAction(
              () => leaveDeck(db, user.uid, deckId),
              'You left the deck.'
            )}
            onArchive={() => runAction(
              () => archiveUserDeck(db, user.uid, deckId),
              'Deck archived. Restore it from Archived Decks on the home page.',
              { navigateHome: true }
            )}
            onRename={name => runAction(() => renameDeck(db, deckId, name), 'Deck renamed.')}
            onVisibility={value => runAction(
              () => updateDeckVisibility(db, deckId, value),
              `Deck is now ${value ? 'public' : 'private'}.`
            )}
            onPace={(pace, dueDate) => runAction(
              () => updateDeckPace(db, deckId, pace, dueDate),
              'Pace updated.'
            )}
            onAdminRole={(member, makeAdmin) => {
              if (member.id === deck.created_by) {
                showToast('The deck creator always keeps admin access.');
                return;
              }
              runAction(
                () => updateDeckAdminRole(db, deckId, member, makeAdmin),
                makeAdmin ? 'Admin granted.' : 'Admin revoked.'
              );
            }}
            onRemoveMember={member => {
              if (member.id === deck.created_by) {
                showToast('The deck creator cannot be removed.');
                return;
              }
              runAction(
                () => removeDeckMember(db, deckId, member.id),
                'Member removed.'
              );
            }}
            onRewriteVerses={verses => runAction(
              () => rewriteDeckVerses(db, deckId, verses),
              'Deck verses updated.'
            )}
            showToast={showToast}
          />
        )}
      </main>
      <nav
        aria-label="Deck sections"
        style={{
          flexShrink: 0,
          display: 'flex',
          borderTop: '1px solid var(--md-sys-color-outline-variant)',
          backgroundColor: 'var(--md-sys-color-surface-container)',
        }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            aria-current={activeTab === tab.id ? 'page' : undefined}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              border: 0,
              padding: '8px 2px',
              background: 'transparent',
              color: activeTab === tab.id
                ? 'var(--md-sys-color-primary)'
                : 'var(--md-sys-color-on-surface-variant)',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Icon name={tab.icon} style={{ display: 'block', margin: '0 auto 3px' }} />
            <span style={{ fontSize: '11px', fontWeight: activeTab === tab.id ? 600 : 400 }}>
              {tab.label}
            </span>
          </button>
        ))}
      </nav>
      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '82px',
            transform: 'translateX(-50%)',
            padding: '12px 20px',
            borderRadius: '24px',
            backgroundColor: 'var(--md-sys-color-inverse-surface)',
            color: 'var(--md-sys-color-inverse-on-surface)',
            zIndex: 1000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
